import { Locale, locales } from '../types';

// Language family mappings for special cases
const LANGUAGE_MAPPINGS: Record<string, Locale> = {
  // Portuguese variants - map to specific supported variants
  pt: 'pt-PT',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-PT',

  // Norwegian variants
  nn: 'nb',
  no: 'nb',
  'nn-no': 'nb',
  'no-no': 'nb',

  // Chinese variants
  zh: 'zh-CN',
  'zh-hans': 'zh-CN',
  'zh-sg': 'zh-CN',
  'zh-my': 'zh-CN',
  'zh-hk': 'zh-TW',
  'zh-hant': 'zh-TW',
};

function isSupportedLocale(locale: string): locale is Locale {
  return new Set(locales).has(locale as Locale);
}

/**
 * Maps any locale string to our supported locales
 * Following a fallback chain:
 * 1. Direct match with supported languages
 * 2. Match from language mapping table
 * 3. Base language match (e.g., "en-US" → "en")
 * 4. Fall back to English
 */
export function mapToSupportedLocale(locale: string): Locale {
  if (!locale) {
    console.warn('No locale string provided. Defaulting to English.');
    return 'en';
  }
  // direct match
  if (isSupportedLocale(locale)) {
    return locale;
  }

  // Normalize the locale to lowercase
  const normalizedLocale = locale.toLowerCase().trim();

  // normalized match
  if (isSupportedLocale(normalizedLocale)) {
    return normalizedLocale;
  }

  // Check predefined mappings
  if (LANGUAGE_MAPPINGS[normalizedLocale]) {
    return LANGUAGE_MAPPINGS[normalizedLocale];
  }

  // Try base language match first
  const baseLanguage = normalizedLocale.split('-')[0];
  if (locales.includes(baseLanguage as Locale)) {
    return baseLanguage as Locale;
  }

  // Try to find any variant of this base language
  const supportedVariant = locales.find((lang) =>
    lang.startsWith(baseLanguage + '-')
  );

  if (supportedVariant) {
    return supportedVariant;
  }

  // Fallback to English
  return 'en' as const;
}
